// src/components/metaAdmin/ConfigurarTeto.js — painel lateral "Configurar teto".
//
// Out/26, Administração Meta fase 3. Fiel ao ConfigurarTetoPanel.dc.html,
// com o que a v1 decidiu de diferente:
//   - Canal só o sino do dashboard; e-mail aparece como "em breve" (fora da
//     v1 — o pedido foi sino primeiro).
//   - Quem recebe: todos os supervisores da empresa, sem lista de e-mails
//     (o design tinha campo de e-mail, que só faz sentido com o canal).
//   - Qualquer supervisor salva (o design previa só o administrador).
//
// O servidor devolve o cartão inteiro no salvar e no remover: a aba Teto, a
// pílula e o bloqueio mudam juntos, sem segunda requisição.
import { useEffect, useRef, useState } from 'react';
import { Box, Drawer } from '@mui/material';
import { FiBell, FiMail, FiUsers, FiX } from 'react-icons/fi';
import api from '../../services/Api';
import { C, FONT_NUM, FONT_TXT } from './tema';

const MARCAS = [50, 80, 100];

// "1.500,50" e "1.500" são mil e quinhentos; "99.9" é noventa e nove e
// noventa. A MESMA regra do servidor (meta_teto.salvar) — a ajuda aqui não
// pode mostrar uma porcentagem e o servidor gravar outro limite.
export function lerLimite(txt) {
  const s = String(txt ?? '').replace('R$', '').trim();
  if (!s) return NaN;
  const ptBr = s.includes(',') || /^\d{1,3}(\.\d{3})+$/.test(s);
  const n = Number(ptBr ? s.replace(/\./g, '').replace(',', '.') : s);
  return Number.isFinite(n) ? n : NaN;
}

const reais = (v) => `R$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const pilula = (on) => (on
  ? { color: C.green, bgcolor: C.greenDim, borderColor: C.greenBdr }
  : { color: C.text2, bgcolor: 'transparent', borderColor: C.border });

const titulo = {
  fontSize: '0.62rem', fontWeight: 700, letterSpacing: '.16em', color: C.text2, textTransform: 'uppercase', mb: '8px',
};
const foco = { '&:focus-visible': { outline: `2px solid ${C.green}`, outlineOffset: 2 } };
// Com o dedo (celular e tablet), 40px de altura mínima em todo botão — o
// design, pensado pro mouse, tinha 27–30px. Pelo tipo de ponteiro, não pela
// largura: tablet tem o painel lateral do desktop, mas é tocado.
const DEDO = '@media (pointer: coarse)';
const toque = { [DEDO]: { minHeight: 40 } };

export default function ConfigurarTeto({ aberto, t, onFechar, onSalvo }) {
  const [limite, setLimite] = useState('');
  const [escopo, setEscopo] = useState('empresa');
  const [chip, setChip] = useState(null);
  const [marc, setMarc] = useState({ 50: true, 80: true, 100: true });
  const [bloqueio, setBloqueio] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [salvo, setSalvo] = useState(false);
  const [removendo, setRemovendo] = useState(false);
  const [confirmarRemocao, setConfirmarRemocao] = useState(false);
  const [erro, setErro] = useState('');
  const fechar = useRef(null);

  // Abre sempre com o que está gravado — fechar sem salvar descarta.
  useEffect(() => {
    if (!aberto || !t) return;
    const cfg = t.config || {};
    setLimite(cfg.limite || '');
    setEscopo(cfg.escopo || 'empresa');
    setChip(cfg.device_id || t.chips?.[0]?.id || null);
    const ligados = new Set(cfg.marcadores || MARCAS);
    setMarc({ 50: ligados.has(50), 80: ligados.has(80), 100: ligados.has(100) });
    setBloqueio(cfg.bloquear !== false);
    setErro('');
    setSalvo(false);
    setConfirmarRemocao(false);
    // Só ao abrir: o cartão relê a cada 60 s e não pode apagar o que a
    // pessoa está digitando.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto]);

  useEffect(() => () => clearTimeout(fechar.current), []);

  if (!t) return null;
  const valor = lerLimite(limite);
  const chipEscolhido = (t.chips || []).find((c) => c.id === chip);
  const gasto = escopo === 'chip' ? (chipEscolhido?.consumido || 0) : (t.consumido_empresa || 0);
  let ajuda = 'Informe um valor em reais.';
  if (valor > 0) {
    ajuda = t.lido === false
      ? 'O consumo do mês aparece aqui depois da primeira leitura da Meta.'
      : `Custo estimado de ${t.mes} até agora${escopo === 'chip' && chipEscolhido ? ` no chip ${chipEscolhido.nome}` : ''}: ${reais(gasto)} (${Math.round((gasto / valor) * 100)}% deste limite).`;
  }

  const ocupado = salvando || removendo || salvo;

  const salvar = async () => {
    if (ocupado) return;
    if (!(valor > 0)) { setErro('Informe o limite mensal em reais.'); return; }
    if (escopo === 'chip' && !chip) { setErro('Escolha o chip.'); return; }
    setSalvando(true);
    setErro('');
    try {
      const { data } = await api.put('/meta-admin/caps', {
        limite,
        escopo,
        device_id: escopo === 'chip' ? chip : null,
        marcadores: MARCAS.filter((v) => marc[v]),
        bloquear: bloqueio,
      });
      onSalvo?.(data);
      setSalvo(true);
      // "Salvar teto" vira "Teto salvo" por um instante antes de fechar.
      fechar.current = setTimeout(() => onFechar?.(), 900);
    } catch (err) {
      setErro(err?.response?.data?.detail || 'Não foi possível salvar o teto. Tente de novo.');
    } finally {
      setSalvando(false);
    }
  };

  const remover = async () => {
    if (ocupado) return;
    if (!confirmarRemocao) { setConfirmarRemocao(true); return; }
    setRemovendo(true);
    setErro('');
    try {
      const { data } = await api.delete('/meta-admin/caps');
      onSalvo?.(data);
      onFechar?.();
    } catch (err) {
      setErro(err?.response?.data?.detail || 'Não foi possível remover o teto. Tente de novo.');
    } finally {
      setRemovendo(false);
    }
  };

  return (
    <Drawer
      anchor="right" open={aberto} onClose={() => !ocupado && onFechar?.()}
      PaperProps={{
        'data-screen-label': 'Configurar teto',
        sx: {
          // border-box: com a borda de 1px por fora, no celular o painel
          // ficava 1px além da tela.
          width: { xs: '100%', sm: 400 }, boxSizing: 'border-box', bgcolor: C.bg2, color: C.text, fontFamily: FONT_TXT,
          borderLeft: `1px solid ${C.border2}`, boxShadow: '-18px 0 48px rgba(0,0,0,0.45)',
          display: 'flex', flexDirection: 'column',
        },
      }}
    >
      <Box sx={{ p: '14px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ fontSize: '0.55rem', fontWeight: 700, letterSpacing: '.1em', color: C.text3, textTransform: 'uppercase', lineHeight: 1.5 }}>Administração Meta · Teto</Box>
          <Box component="h2" sx={{ m: 0, fontSize: '0.95rem', fontWeight: 700, lineHeight: 1.4 }}>Configurar teto</Box>
        </Box>
        <Box component="button" type="button" aria-label="Fechar" onClick={() => !ocupado && onFechar?.()}
          sx={{ width: 30, height: 30, [DEDO]: { width: 40, height: 40 }, borderRadius: '7px', border: `1px solid ${C.border}`, background: 'transparent', color: C.text2, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', p: 0, '&:hover': { color: C.text, bgcolor: 'rgba(255,255,255,0.04)' }, ...foco }}>
          <FiX size={15} />
        </Box>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: '20px', display: 'flex', flexDirection: 'column', gap: '22px', scrollbarWidth: 'thin', scrollbarColor: `${C.bg4} transparent` }}>
        <Box>
          <Box component="label" htmlFor="teto-limite" sx={{ ...titulo, display: 'block' }}>Limite mensal</Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', height: 44, boxSizing: 'border-box', bgcolor: C.bg3, border: '1px solid rgba(174,181,193,0.25)', borderRadius: '8px', px: '12px', '&:focus-within': { borderColor: C.greenBdr } }}>
            <Box component="span" sx={{ fontFamily: FONT_NUM, fontSize: '0.95rem', color: C.text2 }}>R$</Box>
            <Box component="input" id="teto-limite" value={limite} inputMode="decimal" autoComplete="off" placeholder="0,00"
              onChange={(e) => { setLimite(e.target.value); setErro(''); }}
              onKeyDown={(e) => { if (e.key === 'Enter') salvar(); }}
              sx={{ flex: 1, minWidth: 0, height: '100%', background: 'transparent', border: 'none', outline: 'none', color: C.text, fontFamily: FONT_NUM, fontSize: '1.1rem', fontWeight: 700, '&::placeholder': { color: C.text3 } }} />
          </Box>
          <Box sx={{ fontSize: '0.68rem', color: C.text2, mt: '7px', lineHeight: 1.5 }}>{ajuda}</Box>
        </Box>

        <Box>
          <Box sx={titulo}>Aplicar a</Box>
          <Box role="radiogroup" aria-label="Aplicar a" sx={{ display: 'flex', gap: '3px', p: '3px', border: `1px solid ${C.border}`, borderRadius: '8px' }}>
            {[['empresa', 'Empresa inteira'], ['chip', 'Chip específico']].map(([id, rotulo]) => (
              <Box key={id} component="button" type="button" role="radio" aria-checked={escopo === id}
                onClick={() => { setEscopo(id); setErro(''); }}
                sx={{ flex: 1, p: '7px 10px', borderRadius: '6px', border: '1px solid', fontFamily: FONT_TXT, fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer', ...toque, ...pilula(escopo === id), ...foco }}>
                {rotulo}
              </Box>
            ))}
          </Box>
          {escopo === 'chip' && (
            <Box role="radiogroup" aria-label="Chip" sx={{ display: 'flex', flexDirection: 'column', gap: '6px', mt: '8px' }}>
              {(t.chips || []).map((c) => {
                const on = c.id === chip;
                return (
                  <Box key={c.id} component="button" type="button" role="radio" aria-checked={on} onClick={() => setChip(c.id)}
                    sx={{ display: 'flex', alignItems: 'center', gap: '10px', p: '9px 12px', borderRadius: '7px', cursor: 'pointer', textAlign: 'left', fontFamily: FONT_TXT, ...toque,
                      bgcolor: on ? 'rgba(0,214,124,0.06)' : 'transparent', border: `1px solid ${on ? C.greenBdr : C.border}`, ...foco }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', boxSizing: 'border-box', border: `1.5px solid ${on ? C.green : C.text3}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: on ? C.green : 'transparent' }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0, fontSize: '0.75rem', fontWeight: 700, color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.nome}</Box>
                    <Box sx={{ fontFamily: FONT_NUM, fontSize: '0.65rem', color: C.text2, whiteSpace: 'nowrap' }}>{c.numero}</Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>

        <Box>
          <Box sx={titulo}>Avisar em</Box>
          <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {MARCAS.map((v) => (
              <Box key={v} component="button" type="button" aria-pressed={!!marc[v]} onClick={() => setMarc((m) => ({ ...m, [v]: !m[v] }))}
                sx={{ fontFamily: FONT_NUM, fontSize: '0.75rem', fontWeight: 700, p: '6px 14px', [DEDO]: { minHeight: 40, minWidth: 64 }, borderRadius: '20px', border: '1px solid', cursor: 'pointer', ...pilula(marc[v]), ...foco }}>
                {v}%
              </Box>
            ))}
          </Box>
        </Box>

        <Box>
          <Box sx={titulo}>Canais</Box>
          <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.7rem', fontWeight: 600, p: '6px 12px', borderRadius: '20px', border: '1px solid', ...pilula(true) }}>
              <FiBell size={12} /> Sino do dashboard
            </Box>
            <Box aria-disabled="true" sx={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.7rem', fontWeight: 600, p: '6px 12px', borderRadius: '20px', border: `1px dashed ${C.border2}`, color: C.text3 }}>
              <FiMail size={12} /> E-mail · em breve
            </Box>
          </Box>
        </Box>

        <Box>
          <Box sx={titulo}>Quem recebe o aviso</Box>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '7px', p: '5px 12px', borderRadius: '20px', bgcolor: C.bg3, border: `1px solid ${C.border}`, fontSize: '0.7rem', color: C.text }}>
            <Box sx={{ display: 'flex', color: C.text2 }}><FiUsers size={12} /></Box> Todos os supervisores da empresa
          </Box>
        </Box>

        <Box sx={{ border: `1px solid ${C.border}`, borderRadius: '8px', p: '14px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Box id="teto-bloqueio" sx={{ flex: 1, fontSize: '0.78rem', fontWeight: 700, color: C.text, lineHeight: 1.4 }}>Bloquear campanhas ao atingir 100%</Box>
            <Box component="button" type="button" role="switch" aria-checked={bloqueio} aria-labelledby="teto-bloqueio" onClick={() => setBloqueio((b) => !b)}
              sx={{ position: 'relative', width: 36, height: 20, borderRadius: '10px', border: 'none', p: 0, cursor: 'pointer', flexShrink: 0, transition: 'background .15s', bgcolor: bloqueio ? C.green : C.bg4, ...foco,
                // O desenho fica 36×20; o toque pega 10px em volta.
                '&::after': { content: '""', position: 'absolute', inset: '-10px -6px' } }}>
              <Box component="span" sx={{ position: 'absolute', top: 2, width: 16, height: 16, borderRadius: '50%', transition: 'left .15s', left: bloqueio ? 18 : 2, bgcolor: bloqueio ? '#060d14' : C.text2 }} />
            </Box>
          </Box>
          <Box sx={{ fontSize: '0.7rem', color: C.text2, lineHeight: 1.55, mt: '8px', textWrap: 'pretty' }}>
            O bloqueio atinge apenas envio em lote e disparo agendado, inclusive pelo Copiloto, até o fim do mês.{' '}
            <Box component="b" sx={{ color: C.text }}>Resposta a cliente nunca é bloqueada.</Box>
          </Box>
        </Box>
      </Box>

      {erro && (
        <Box role="alert" sx={{ mx: '20px', mb: '10px', p: '8px 12px', borderRadius: '6px', bgcolor: C.redDim, border: `1px solid ${C.redBdr}`, color: C.red, fontSize: '0.7rem', lineHeight: 1.45 }}>
          {erro}
        </Box>
      )}

      {/* `safe-area-inset-bottom`: no iPhone sem botão, o rodapé ficaria
          embaixo da barra de gesto. */}
      <Box sx={{ p: '14px 20px', pb: 'calc(14px + env(safe-area-inset-bottom, 0px))', borderTop: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
        {t.configurado && (
          <Box component="button" type="button" onClick={remover} disabled={ocupado}
            sx={{ fontFamily: FONT_TXT, fontSize: '0.7rem', fontWeight: 600, color: confirmarRemocao ? C.red : C.text2, background: 'transparent', border: 'none', p: '7px 0', cursor: 'pointer', textAlign: 'left', ...toque, '&:hover': { color: C.red }, '&:disabled': { cursor: 'default', opacity: 0.6 }, ...foco }}>
            {removendo ? 'Removendo…' : confirmarRemocao ? 'Confirmar: remover teto' : 'Remover teto'}
          </Box>
        )}
        <Box sx={{ flex: 1 }} />
        <Box component="button" type="button" onClick={() => !ocupado && onFechar?.()}
          sx={{ fontFamily: FONT_TXT, fontSize: '0.7rem', fontWeight: 600, color: C.text2, background: 'transparent', border: '1px solid transparent', borderRadius: '6px', p: '7px 14px', cursor: 'pointer', whiteSpace: 'nowrap', ...toque, '&:hover': { color: C.text, bgcolor: 'rgba(255,255,255,0.04)' }, ...foco }}>
          Cancelar
        </Box>
        <Box component="button" type="button" onClick={salvar} disabled={ocupado}
          sx={{ fontFamily: FONT_TXT, fontSize: '0.7rem', fontWeight: 700, letterSpacing: '.02em', color: '#060d14', bgcolor: C.green, border: 'none', borderRadius: '6px', p: '7px 16px', cursor: 'pointer', whiteSpace: 'nowrap', ...toque, '&:hover': { bgcolor: C.greenHover }, '&:disabled': { cursor: 'default' }, ...foco }}>
          {salvo ? 'Teto salvo' : salvando ? 'Salvando…' : 'Salvar teto'}
        </Box>
      </Box>
    </Drawer>
  );
}
